import * as React from 'react';
import clsx from 'clsx';
import { useId } from '@wartek-id/react-utils';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Textarea-module_textarea__2Izl3{--padding-left-input-area:16px;--padding-right-input-area:16px;box-sizing:border-box;font-size:var(--size-font-body);line-height:var(--size-line-height-text-body);font-family:Inter,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Open Sans,Helvetica Neue,sans-serif;padding:12px 16px;background-color:var(--color-white);color:var(--color-text-default);border:1px solid var(--color-border-default);border-radius:var(--size-border-radius-base);outline:none;width:100%;resize:none}.Textarea-module_textarea__2Izl3::-moz-placeholder{color:var(--color-text-disabled)}.Textarea-module_textarea__2Izl3:-ms-input-placeholder{color:var(--color-text-disabled)}.Textarea-module_textarea__2Izl3::placeholder{color:var(--color-text-disabled)}.Textarea-module_textarea__2Izl3.Textarea-module_error__gOCxF:focus,.Textarea-module_textarea__2Izl3:focus{box-shadow:0 0 0 2px var(--color-interactive-focused);border-color:var(--color-interactive-focused)}.Textarea-module_textarea__2Izl3.Textarea-module_disabled__21E2z,.Textarea-module_textarea__2Izl3[aria-disabled=true]{background-color:var(--color-surface-disabled);border-color:var(--color-border-disabled);color:var(--color-text-disabled)}.Textarea-module_textarea__2Izl3.Textarea-module_error__gOCxF{background-color:var(--color-surface-critical-subdued);border-color:var(--color-border-critical-default)}.Textarea-module_helperContainer__1HNT3{display:flex;justify-content:space-between;margin-top:4px}.Textarea-module_helper__3polw{font-size:var(--size-font-helper);font-weight:var(--font-weight-helper);line-height:var(--size-line-height-text-helper);flex-basis:100%}.Textarea-module_helper__3polw.Textarea-module_default__1C2zh{color:var(--color-text-subdued)}.Textarea-module_helper__3polw.Textarea-module_error__gOCxF{color:var(--color-text-critical)}.Textarea-module_helper__3polw.Textarea-module_right__1uXXB{text-align:right}.Textarea-module_helper__3polw+.Textarea-module_right__1uXXB{flex-basis:24px}";
var styles = {"textarea":"Textarea-module_textarea__2Izl3","error":"Textarea-module_error__gOCxF","disabled":"Textarea-module_disabled__21E2z","helperContainer":"Textarea-module_helperContainer__1HNT3","helper":"Textarea-module_helper__3polw","default":"Textarea-module_default__1C2zh","right":"Textarea-module_right__1uXXB","$default$":"Textarea-module_default__1C2zh"};
styleInject(css_248z);

var Textarea = React.forwardRef(function (_a, ref) {
    var _b;
    var className = _a.className, _c = _a.isDisabled, isDisabled = _c === void 0 ? false : _c, _d = _a.isInvalid, isInvalid = _d === void 0 ? false : _d, helperText = _a.helperText, errorMessage = _a.errorMessage, maxLength = _a.maxLength; _a.value; var onChange = _a.onChange, props = __rest(_a, ["className", "isDisabled", "isInvalid", "helperText", "errorMessage", "maxLength", "value", "onChange"]);
    var classes = clsx(className, styles.textarea, isDisabled && styles.disabled, !!isInvalid && styles.error);
    var helperId = useId();
    var internalRef = React.useRef(null);
    var _e = React.useState(0), inputLength = _e[0], setInputLength = _e[1];
    var handleChange = function (e) {
        setInputLength(e.target.value.length);
        onChange === null || onChange === void 0 ? void 0 : onChange(e);
    };
    var assignRef = function (node) {
        // @ts-expect-error
        internalRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        }
        else if (ref) {
            ref.current = node;
        }
    };
    // Make input length counter consistent by ready from internalRef
    // Note: we can't use props.defaultValue as it causes the value not
    // displayed on the screen for some reason
    React.useEffect(function () {
        var _a, _b;
        if ((_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.value) {
            var length_1 = String((_b = internalRef.current) === null || _b === void 0 ? void 0 : _b.value).length;
            setInputLength(length_1);
        }
    }, [(_b = internalRef.current) === null || _b === void 0 ? void 0 : _b.value]);
    return (React.createElement("div", { style: { width: '100%', lineHeight: 0 } },
        React.createElement("textarea", __assign({ "aria-disabled": isDisabled, disabled: isDisabled, "aria-invalid": !!isInvalid, className: classes, rows: 5, maxLength: maxLength, onChange: handleChange, "aria-describedby": helperId }, props, { ref: assignRef })),
        (helperText || errorMessage || typeof maxLength !== 'undefined') && (React.createElement("div", { className: styles.helperContainer },
            (helperText || errorMessage) && (React.createElement("span", { id: helperId, className: clsx(styles.helper, helperText && styles.default, (!!isInvalid || errorMessage) && styles.error), "aria-live": !!isInvalid ? 'polite' : 'off' }, errorMessage || helperText)),
            typeof maxLength !== 'undefined' && maxLength > 0 && (React.createElement("span", { className: clsx(styles.helper, styles.right, helperText && styles.default, errorMessage && styles.error) },
                inputLength,
                "/",
                maxLength))))));
});

export { Textarea };
